import React, { useEffect, useRef, useState } from "react";
import { getBranchQrSalons, setBranchSalonQrAccess } from "../../services/supabase/qrSalonAccessService";

const buttonStyle = { padding: "10px 14px", border: "1px solid #b7c4cf", borderRadius: 6, background: "#fff", color: "#203447", fontWeight: 700, cursor: "pointer" };

export default function QrSalonAccessPanel({ branchId, tables, hours, canManage, disabled, onUpdated, onBusy }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  const [revision, setRevision] = useState(0);
  const [, setClock] = useState(0);
  const busyRef = useRef(false);
  const requestVersion = useRef(0);
  const mounted = useRef(true);
  const propsRef = useRef({ branchId, onUpdated, onBusy, disabled });
  propsRef.current = { branchId, onUpdated, onBusy, disabled };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (disabled) requestVersion.current += 1; }, [disabled]);
  useEffect(() => {
    let active = true;
    let pending = false;
    setRooms([]); setMessage(null); setLoading(true);
    const refresh = async () => {
      if (pending || busyRef.current || !branchId) { if (!branchId) setLoading(false); return; }
      pending = true;
      const version = requestVersion.current;
      const startedDisabled = propsRef.current.disabled;
      try {
        const result = await getBranchQrSalons(branchId);
        if (active && !busyRef.current && version === requestVersion.current) {
          setRooms(result.rooms);
          if (!startedDisabled && !propsRef.current.disabled) propsRef.current.onUpdated(result.qrs);
          setMessage((previous) => previous?.error ? null : previous);
        }
      } catch (error) { if (active && version === requestVersion.current && !busyRef.current) setMessage({ error: true, text: error.message }); }
      finally { pending = false; if (active) setLoading(false); }
    };
    void refresh();
    const timer = window.setInterval(() => { setClock((value) => value + 1); void refresh(); }, 30000);
    window.addEventListener("focus", refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [branchId, revision]);

  const changeAccess = async (room, enabled) => {
    if (!canManage || disabled || busyRef.current) return;
    if (!enabled && !window.confirm(`Cerrar los accesos QR de ${room.name}? Se cerraran las sesiones QR de sus mesas. Los pedidos registrados no se eliminan.`)) return;
    requestVersion.current += 1;
    busyRef.current = true; setBusy(room.id); setMessage(null); onBusy(true);
    try {
      const result = await setBranchSalonQrAccess(branchId, room.id, enabled, hours * 60);
      if (!mounted.current || propsRef.current.branchId !== branchId) return;
      setRooms(result.rooms); propsRef.current.onUpdated(result.qrs);
      setMessage({ error: false, text: enabled
        ? `${result.roomName}: ${result.affectedCount} ${result.affectedCount === 1 ? "mesa habilitada" : "mesas habilitadas"} por ${hours}h. Los otros salones no cambiaron.`
        : `${result.roomName}: accesos QR cerrados. Los otros salones no cambiaron.` });
    } catch (error) { if (mounted.current && propsRef.current.branchId === branchId) setMessage({ error: true, text: error.message }); }
    finally {
      busyRef.current = false;
      if (mounted.current) {
        setBusy(""); propsRef.current.onBusy(false);
        if (propsRef.current.branchId !== branchId) setRevision((value) => value + 1);
      }
    }
  };
  const byId = new Map(tables.map((table) => [table.id, table]));
  return <section style={{ borderTop: "1px solid #d4dce3", borderBottom: "1px solid #d4dce3", padding: "18px 0", color: "#203447", minWidth: 0 }}>
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 20, color: "#203447" }}>Acceso QR por salon</h2>
      <button type="button" style={buttonStyle} disabled={Boolean(busy) || loading} onClick={() => setRevision((value) => value + 1)}>Actualizar salones</button>
    </div>
    {loading ? <p role="status">Cargando salones...</p> : rooms.map((room) => {
      const roomTables = room.tableIds.map((id) => byId.get(id)).filter(Boolean);
      const enabledCount = roomTables.filter((table) => Date.parse(table.qr?.accessEnabledUntil || "") > Date.now()).length;
      const allEnabled = enabledCount > 0 && enabledCount === room.tableIds.length;
      const blocked = disabled || !canManage || Boolean(busy) || !room.tableIds.length;
      return <div key={room.id} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between", gap: 12, padding: "16px 0", borderTop: "1px solid #e0e6eb", marginTop: 12 }}>
        <div style={{ flex: "1 1 220px", minWidth: 0, overflowWrap: "anywhere" }}>
          <strong style={{ fontSize: 17 }}>{room.name}</strong>
          <div style={{ marginTop: 5, color: enabledCount ? "#146c43" : "#526170" }}>{enabledCount} de {room.tableIds.length} QR habilitados</div>
          <div style={{ marginTop: 5, fontSize: 14, color: "#526170" }}>{roomTables.length
            ? roomTables.slice(0, 6).map((table) => table.label || `Mesa ${table.tableNumber}`).join(", ") + (roomTables.length > 6 ? ` y ${roomTables.length - 6} mas` : "")
            : "Sin mesas con QR"}</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button type="button" style={{ ...buttonStyle, background: "#174ea6", borderColor: "#174ea6", color: "#fff", opacity: blocked ? 0.5 : 1 }}
            aria-label={`${allEnabled ? "Renovar" : "Habilitar"} QR de ${room.name}`} disabled={blocked} onClick={() => changeAccess(room, true)}>
            {busy === room.id ? "Actualizando..." : `${allEnabled ? "Renovar" : "Habilitar"} salon ${hours}h`}
          </button>
          <button type="button" style={{ ...buttonStyle, opacity: blocked || enabledCount === 0 ? 0.5 : 1 }} aria-label={`Cerrar QR de ${room.name}`}
            disabled={blocked || enabledCount === 0} onClick={() => changeAccess(room, false)}>Cerrar QR del salon</button>
        </div>
      </div>;
    })}
    {message ? <p role={message.error ? "alert" : "status"} style={{ marginBottom: 0, color: message.error ? "#b42318" : "#146c43", overflowWrap: "anywhere" }}>{message.text}</p> : null}
  </section>;
}
